import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Fingerprint, Loader2 } from 'lucide-react';
import { isWebAuthnSupported } from '@/hooks/security/useBiometricAuth';
import { startAuthentication } from '@simplewebauthn/browser';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';

interface PasskeyLoginButtonProps {
  onSuccess?: () => void;
  disabled?: boolean;
}

const PasskeyLoginButton = ({ onSuccess, disabled }: PasskeyLoginButtonProps) => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const handlePasskeyLogin = async () => {
    if (!isWebAuthnSupported()) {
      toast.error('Passkeys are not supported on this device');
      return;
    }

    setIsAuthenticating(true);

    try {
      // Get authentication challenge (no email needed - uses discoverable credentials)
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: { action: 'get-challenge', hostname: window.location.hostname }
      });

      if (challengeError) {
        throw new Error(challengeError.message || 'Failed to get authentication challenge');
      }

      if (!challengeData) {
        throw new Error('Failed to initialize passkey authentication');
      }

      // Start WebAuthn authentication - browser will show all available passkeys
      const credential = await startAuthentication({
        optionsJSON: challengeData
      });

      // Verify credential on server - send current origin for redirect
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('webauthn-authenticate', {
        body: {
          action: 'verify',
          credentialId: credential.id,
          authenticatorData: credential.response.authenticatorData,
          clientDataJSON: credential.response.clientDataJSON,
          signature: credential.response.signature,
          redirectUrl: window.location.origin,
          hostname: window.location.hostname,
        }
      });

      if (verifyError || !verifyData?.success) {
        throw new Error(verifyError?.message || 'Authentication failed');
      }

      // If we got a magic link URL, redirect to it
      if (verifyData.url) {
        window.location.href = verifyData.url;
        return;
      }

      toast.success('Signed in with passkey');
      onSuccess?.();
    } catch (error: any) {
      console.error('Passkey login error:', error);

      if (error.name === 'NotAllowedError') {
        toast.error('Authentication was cancelled');
      } else if (error.name === 'InvalidStateError') {
        toast.error('No passkeys available for this site');
      } else {
        toast.error(error.message || 'Failed to sign in with passkey');
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Don't render if WebAuthn is not supported
  if (!isWebAuthnSupported()) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={handlePasskeyLogin}
      disabled={disabled || isAuthenticating}
    >
      {isAuthenticating ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Authenticating...
        </>
      ) : (
        <>
          <Fingerprint className="mr-2 h-4 w-4" />
          Sign in with passkey
        </>
      )}
    </Button>
  );
};

export default PasskeyLoginButton;
